import { LicensingAgencyStatus } from '@/@types/generated/models/LicensingAgencyStatus'
import { hasRole } from '@/components/Atoms/Permissions'
import { Button } from '@/components/ui/button'
import { helpEmail } from '@/data/texas'
import { useAuth } from '@/hooks/useAuth'
import { InternalRoles } from '@/lib/utils'
import { ArrowRightIcon, XCircleIcon } from 'lucide-react'
import { useLicensingAgency } from '../hooks/licensing-agency-provider'
import { Link } from 'react-router'
import { buildPath } from '@/routes'

const NEXT_STEPS = [
  {
    action: 'Upload a different EIN confirmation letter.',
    detail:
      'Re-check the document on file and provide a current, legible copy.',
  },
  {
    action: 'Review the rest of your application for accuracy.',
    detail:
      'Confirm your legal name, address, and ownership details all match your supporting documents.',
  },
  {
    action: 'Contact the DMV for details on this decision.',
    detail:
      'Reach the licensing team below if you need clarification before resubmitting.',
  },
]

export function LicensingAgencyRejectedAlert() {
  // Hooks
  const { user } = useAuth()
  const { licensingAgency } = useLicensingAgency()

  // Not rejected, nothing to show
  if (licensingAgency?.status !== LicensingAgencyStatus.REJECTED) return null

  // This guidance is for the applicant only
  if (hasRole(InternalRoles, user)) return null

  return (
    <div className="border-destructive bg-destructive-background flex flex-col gap-4 rounded-xl border p-6">
      <div className="flex items-start gap-4">
        <XCircleIcon className="stroke-destructive size-14" />

        <div className="flex flex-col gap-1">
          <span className="text-destructive text-lg font-bold">
            Updates Needed Before Resubmission
          </span>
          <span className="text-destructive/90">
            Your application wasn&apos;t approved in its current form. Review
            the items below, update your application, and resubmit for another
            review.
          </span>
        </div>
      </div>

      <div className="bg-background flex flex-col gap-4 rounded-xl border p-6">
        <span className="text-destructive text-sm font-bold tracking-wide uppercase">
          What To Do Next
        </span>

        <ul className="flex flex-col gap-3">
          {NEXT_STEPS.map(({ action, detail }) => (
            <li key={action} className="flex items-start gap-2">
              <XCircleIcon className="stroke-destructive mt-1 size-4 shrink-0" />
              <span>
                <span className="font-semibold">{action}</span>{' '}
                <span className="text-muted-foreground">{detail}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button variant="destructive" asChild>
          <Link
            to={buildPath('/applications/licensing-agency/:agencyId', {
              agencyId: licensingAgency.id,
            })}
          >
            Update &amp; Resubmit Application
            <ArrowRightIcon />
          </Link>
        </Button>

        <Button variant="outline" asChild>
          <a href={`mailto:${helpEmail}`}>Contact Support</a>
        </Button>
      </div>
    </div>
  )
}
